/**
 * CR-7732: the haulier's own figures on a call-off — price, pickup date,
 * agreed delivery date and lorry number.
 *
 * The haulier fills them in while the load is Confirmed or Dispatched; the
 * customer reads them and cannot change them; the supplier is never sent them
 * at all, so this renders nothing for it.
 *
 * CR-7781: the values entered here are what the haulier's Confirm Load (in
 * the modal footer) rests on, so unsaved edits are reported up to hold it.
 * Once the load has left the supplier the block says why it is read-only
 * rather than failing a save. While its Admin Setup switch is on, the
 * customer may change the cost — and, CR-8613, every other haulage field — in
 * place, with a reason ({@link CallOffCustomerHaulageCost}).
 */
import { useEffect, useState } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import CallOffCustomerHaulageCost from './CallOffCustomerHaulageCost';

interface CallOffHaulagePanelProps {
  callOff: CallOff;
  /** Reload the call-off and its history after a save. */
  onSaved: () => void;
  /** CR-7781: told when the form gains or loses unsaved edits. */
  onDirtyChange?: (dirty: boolean) => void;
}

interface HaulageForm {
  haulage_cost: string;
  haulier_pickup_date: string;
  haulier_agreed_delivery_date: string;
  lorry_number: string;
}

function toForm(callOff: CallOff): HaulageForm {
  return {
    haulage_cost: callOff.haulage_cost ?? '',
    haulier_pickup_date: callOff.haulier_pickup_date ?? '',
    haulier_agreed_delivery_date: callOff.haulier_agreed_delivery_date ?? '',
    lorry_number: callOff.lorry_number ?? '',
  };
}

const LABELS: Record<keyof HaulageForm, string> = {
  haulage_cost: 'Haulage Cost',
  haulier_pickup_date: 'Pickup Date',
  haulier_agreed_delivery_date: 'Agreed Delivery Date',
  lorry_number: 'Lorry Number',
};

/** The fields shown as dates, in the viewer's format, when read-only. */
const DATE_FIELDS: ReadonlyArray<keyof HaulageForm> = ['haulier_pickup_date', 'haulier_agreed_delivery_date'];

export default function CallOffHaulagePanel({ callOff, onSaved, onDirtyChange }: CallOffHaulagePanelProps) {
  const { formatDate } = useFormatters();
  const [form, setForm] = useState<HaulageForm>(() => toForm(callOff));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // What the server holds now — the read-only view shows this, not the form,
  // so a value saved from elsewhere (CR-7781: the customer's cost) shows at once.
  const current = toForm(callOff);
  const dirty = (Object.keys(form) as (keyof HaulageForm)[])
    .some(field => form[field] !== current[field]);

  // CR-7781: the footer's Confirm Load waits for unsaved edits to be saved.
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // CR-7732: render whatever the server actually sent. It decides who reads
  // what — the supplier is sent the confirmed pickup date, because the
  // collection happens at its site, and none of the rest. Keying the panel on
  // one field would have shown it an empty box or nothing at all.
  const shown = (Object.keys(LABELS) as (keyof HaulageForm)[])
    .filter(field => field in callOff);
  if (shown.length === 0) return null;

  const editable = callOff.can_edit_haulage === true;
  const customerCanChangeCost = callOff.can_customer_edit_haulage_cost === true;
  const set = (field: keyof HaulageForm, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await callOffAPI.saveHaulage(callOff.id, {
        haulage_cost: form.haulage_cost === '' ? null : form.haulage_cost,
        haulier_pickup_date: form.haulier_pickup_date || null,
        haulier_agreed_delivery_date: form.haulier_agreed_delivery_date || null,
        lorry_number: form.lorry_number,
      });
      toast.success('Haulage details saved');
      onSaved();
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, 'Failed to save haulage details');
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-4 border rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium">Haulage</h3>
        {callOff.haulier_name && (
          <span className="text-sm text-muted-foreground">{callOff.haulier_name}</span>
        )}
      </div>

      {editable ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="haulage-cost">{LABELS.haulage_cost}</Label>
              <Input
                id="haulage-cost"
                type="number"
                min="0"
                step="0.01"
                value={form.haulage_cost}
                onChange={(e) => set('haulage_cost', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="lorry-number">{LABELS.lorry_number}</Label>
              <Input
                id="lorry-number"
                value={form.lorry_number}
                onChange={(e) => set('lorry_number', e.target.value)}
                maxLength={50}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="haulier-pickup">{LABELS.haulier_pickup_date}</Label>
              <Input
                id="haulier-pickup"
                type="date"
                value={form.haulier_pickup_date}
                onChange={(e) => set('haulier_pickup_date', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="haulier-delivery">{LABELS.haulier_agreed_delivery_date}</Label>
              <Input
                id="haulier-delivery"
                type="date"
                value={form.haulier_agreed_delivery_date}
                onChange={(e) => set('haulier_agreed_delivery_date', e.target.value)}
              />
            </div>
          </div>
          {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end">
            <Button size="sm" onClick={save} disabled={saving || !dirty}>
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save haulage details
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* CR-7781: say why the block cannot be changed once it has
              locked, rather than leave a read-only box to be mistaken for a
              broken one. Not for a customer that may still change the cost —
              "locked" beside a Change button would contradict itself. */}
          {callOff.haulage_lock_reason && !customerCanChangeCost && (
            <p className="flex items-center gap-1.5 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5 shrink-0" />
              {callOff.haulage_lock_reason}
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            {shown.map(field => (customerCanChangeCost ? (
              // CR-7781 / CR-8613: the customer changes any of the four in
              // place, with a reason.
              <CallOffCustomerHaulageCost key={field} field={field} callOff={callOff} onSaved={onSaved} />
            ) : (
              <div key={field}>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  {LABELS[field]}
                </label>
                <div className="border border-border rounded-lg bg-muted/30 px-3 py-2 font-medium">
                  {current[field]
                    ? (DATE_FIELDS.includes(field) ? formatDate(current[field]) : current[field])
                    : <span className="text-muted-foreground">Not entered</span>}
                </div>
              </div>
            )))}
          </div>
        </div>
      )}
    </div>
  );
}
